<template>
  <div class="ai-drug-alerts" v-if="aiEnabled && alerts.length > 0">
    <div
      class="ai-drug-alert"
      :class="alert.level"
      v-for="(alert, i) in alerts"
      :key="i"
    >
      <fa-icon :icon="['fal', 'exclamation-triangle']" />
      <div>
        <strong v-if="alert.drugs.length > 0">{{ alert.drugs.join(' + ') }}</strong>
        <span>{{ alert.message }}</span>
      </div>
    </div>

    <span class="ai-drug-alerts-disclaimer">
      Checagem por IA. Não substitui a conferência do profissional.
    </span>
  </div>
</template>

<script>
import { toText } from '@/helpers/html';
import ai from '@/mixins/ai';

const LIST_DEBOUNCE = 1500;
const TEXT_DEBOUNCE = 3000;

export default {
  mixins: [ai],
  props: {
    medicalRecordId: {
      type: String,
      default: '',
    },
    drugs: {
      type: Array,
      default: () => [],
    },
    text: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      alerts: [],
      timeout: null,
      signature: '',
    };
  },
  mounted() {
    this.schedule();
  },
  beforeDestroy() {
    clearTimeout(this.timeout);
  },
  watch: {
    drugs: {
      handler() {
        this.schedule();
      },
      deep: true,
    },
    text() {
      this.schedule();
    },
  },
  computed: {
    items() {
      return this.drugs
        .filter(drug => drug && drug.name && drug.name.trim())
        .map(drug => ({ name: drug.name }));
    },
    content() {
      return toText(this.text).trim();
    },
    payload() {
      return this.content
        ? { text: this.content }
        : { drugs: this.items };
    },
    isEmpty() {
      return !this.content && this.items.length === 0;
    },
  },
  methods: {
    schedule() {
      clearTimeout(this.timeout);

      const signature = JSON.stringify(this.payload);

      if (signature === this.signature) {
        return;
      }

      if (this.isEmpty) {
        this.signature = signature;
        this.alerts = [];
        return;
      }

      const debounce = this.content ? TEXT_DEBOUNCE : LIST_DEBOUNCE;

      this.timeout = setTimeout(() => {
        this.signature = signature;
        this.check();
      }, debounce);
    },
    check() {
      if (!this.medicalRecordId || !this.aiEnabled) {
        return Promise.resolve();
      }

      this.$emit('loading', true);

      return this.$httpX
        .post(`/medical-records/${this.medicalRecordId}/assistant/drug-check`, this.payload)
        .then(({ data }) => {
          this.alerts = data.alerts || [];
        })
        .catch(() => {
          this.alerts = [];
        })
        .finally(() => {
          this.$emit('loading', false);
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.ai-drug-alerts {
  margin-bottom: $layout-spacing-lg;

  .ai-drug-alert {
    align-items: flex-start;
    border: $border-width solid;
    border-radius: $border-radius;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing-lg;
    margin-bottom: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;

    strong {
      display: block;
    }

    &.high {
      background: $red-color-100;
      border-color: $red-color-300;
      color: $red-color-700;
    }

    &.medium {
      background: $orange-color-100;
      border-color: $orange-color-300;
      color: $orange-color-700;
    }

    &.low {
      background: $gray-color-100;
      border-color: $border-color;
      color: $gray-color-700;
    }
  }

  .ai-drug-alerts-disclaimer {
    color: $gray-color-600;
    display: block;
    font-size: $font-size-xs;
    padding-left: $layout-spacing-lg;
  }
}
</style>
